import { Button, InlineMessage, PageContent, Spinner, Stack } from '@olytic/foundation';
import {
  blankDefinition,
  normalizeDefinition,
  ReportEditButtons,
  type ReportDefinition,
  type ReportCellRows,
  type ReportMatrixCell,
  type ReportResult,
  ReportRunHeader,
  type ReportType,
  ReportView,
  reportResultFromSummaries,
  SaveReportDialog,
} from '@olytic/foundation/reports';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { REPORTS_ROUTE, SETUP_ROUTES } from '../navigation';
import { loadLookupOptions } from '../objects/lookupOptions';
import { recordHref as objectRecordHref } from '../objects/routes';
import { updateRecord } from '../objects/writes';
import {
  createFolder, createReport, loadReport, markReportRun, type SavedReport, saveReport, useLibrary, withDefaultColumns,
} from './reportStore';
import { cellEdit, type EditTarget, editTargets, withEditors } from './reportEdits';
import { createReportExporter } from './reportExport';
import { REPORT_TIME_ZONE, REPORT_WEEK_STARTS_ON, runReportOnServer, useReportTypes } from './reportSchema';

/**
 * Self-service reports, built from @olytic/foundation/reports. The server runs each
 * definition as one query (POST /api/reports/run, reports.mjs) and Foundation's
 * reportResultFromSummaries shapes its rows and summaries for the report.
 * Saved reports and folders live in memory (reportStore.ts) until the data API
 * stores them.
 */

export { REPORTS_ROUTE };
const TIME_ZONE = REPORT_TIME_ZONE;
const WEEK_STARTS_ON = REPORT_WEEK_STARTS_ON;
/** How long edits settle before the report runs again. */
const RUN_DELAY_MS = 250;

const typeIn = (types: ReportType[] | null, key: string | null | undefined): ReportType | undefined =>
  types?.find((t) => t.key === key);


interface Run {
  result: ReportResult | null;
  /** Set when the server's own row cap, not the report's row limit, cut the detail rows. */
  rowCap: number | null;
  error: string | null;
  /** Whether `result` is this definition's own rows, not the last definition's while the next one runs. */
  current: boolean;
}

/**
 * Runs the definition on the server (POST /api/reports/run), again shortly after each change.
 * `scope` names what the rows belong to beyond the definition (a visit to a saved report), so
 * the same definition opened again is not `current` until it has run again.
 */
function useRun(definition: ReportDefinition | null, type: ReportType | undefined, version = 0, scope = ''): Run {
  const [run, setRun] = useState<Omit<Run, 'current'> & { ranKey: string | null }>({ result: null, rowCap: null, error: null, ranKey: null });
  const key = definition && type ? `${scope}\n${JSON.stringify(definition)}` : null;
  useEffect(() => {
    if (!definition || !type) return undefined;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      runReportOnServer(authenticatedFetch, definition, controller.signal).then(
        (data) => setRun({
          result: reportResultFromSummaries(definition, type, data, { timeZone: TIME_ZONE, weekStartsOn: WEEK_STARTS_ON }),
          rowCap: data.rowCap ?? null,
          error: null,
          ranKey: key,
        }),
        (error: unknown) => {
          if (!controller.signal.aborted) setRun((previous) => ({ ...previous, error: errorText(error) }));
        },
      );
    }, RUN_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // The definition's JSON is the key: a new object with the same content does not re-run.
    // `version` runs it again after a record changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, type, version]);
  const { ranKey, ...rest } = run;
  return { ...rest, current: ranKey !== null && ranKey === key };
}

/**
 * A matrix cell's records, shown below the matrix: the report's filters plus
 * the cell's group values, run on the server a page at a time. A change to
 * the report closes the cell.
 */
function useCellRows(definition: ReportDefinition | null) {
  const [selected, setSelected] = useState<ReportMatrixCell | null>(null);
  const [rows, setRows] = useState<ReportCellRows | null>(null);
  const key = definition ? JSON.stringify(definition) : null;
  useEffect(() => { setSelected(null); setRows(null); }, [key]);
  const load = (cell: ReportMatrixCell, previous: ReportCellRows | null) => {
    if (!definition) return;
    setRows({ rows: previous?.rows ?? [], totalRows: previous?.totalRows ?? 0, loading: true });
    runReportOnServer(authenticatedFetch, definition, undefined, {
      cell: { rows: cell.rowValues, columns: cell.columnValues },
      offset: previous?.rows.length ?? 0,
    }).then(
      (data) => setRows({ rows: [...(previous?.rows ?? []), ...data.rows], totalRows: data.totalRows }),
      (error: unknown) => setRows({ rows: previous?.rows ?? [], totalRows: previous?.totalRows ?? 0, error: errorText(error) }),
    );
  };
  return {
    selected,
    rows: selected ? rows : null,
    select: (cell: ReportMatrixCell | null) => {
      setSelected(cell);
      setRows(null);
      if (cell) load(cell, null);
    },
    more: () => { if (selected && rows && !rows.loading) load(selected, rows); },
    /** Loads the selected cell's records again, after one of them changed. */
    refresh: () => { if (selected) load(selected, null); },
  };
}

/** Report links: object records, and people to their user page. */
const recordHref = (object: string, id: string): string | null =>
  object === 'users' ? `${SETUP_ROUTES.users}/${id}` : objectRecordHref(object, id);

const errorText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.');

/* ── /reports/new, /reports/:id/edit ───────────────────────────── */

export function ReportRunPage(): JSX.Element {
  const { types, schema, error: typesError } = useReportTypes(authenticatedFetch);
  const { library, reload: reloadLibrary } = useLibrary(authenticatedFetch);
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  // /reports/new?type=…: an unsaved report on this same page, starting as a plain list of records.
  const isNew = !id;
  // /reports/:id/edit opens the same page to change the report, which is not opening it to read.
  const editing = useMatch(`${REPORTS_ROUTE}/:id/edit`) !== null;
  // Each load of a report is a new visit, numbered so the visit's run and its open record are its own.
  const [opened, setOpened] = useState<{ id: string; visit: number; report: SavedReport | null; error: string | null } | null>(null);
  const visits = useRef(0);
  useEffect(() => {
    if (isNew) return undefined;
    let live = true;
    visits.current += 1;
    const visit = visits.current;
    loadReport(authenticatedFetch, id).then(
      (report) => { if (live) setOpened({ id, visit, report, error: null }); },
      (err: unknown) => { if (live) setOpened({ id, visit, report: null, error: errorText(err) }); },
    );
    return () => { live = false; };
  }, [id, isNew]);
  const saved = !isNew && opened?.id === id ? opened.report ?? undefined : undefined;
  const visit = saved && opened ? opened.visit : null;
  const loading = !isNew && opened?.id !== id;
  const plainType = typeIn(types, isNew ? params.get('type') : saved?.reportType);
  // The fields the person may change in place, with the editors list views use (reportEdits.ts).
  const targets = useMemo(
    () => (plainType && schema ? editTargets(plainType, schema.objects, schema.writable) : new Map<string, EditTarget>()),
    [plainType, schema],
  );
  const type = useMemo(() => (plainType ? withEditors(plainType, targets, loadLookupOptions) : undefined), [plainType, targets]);
  const [version, setVersion] = useState(0);
  const savedDefinition = useMemo(
    () => (isNew
      ? (type ? blankDefinition(type) : null)
      : saved?.definition && type ? normalizeDefinition(withDefaultColumns(saved.definition, type), type) : null),
    [isNew, saved, type],
  );

  // Groups, Fields, Filters and sorting change a working copy; Save keeps it, Discard drops it.
  const [draft, setDraft] = useState<ReportDefinition | null>(null);
  const [saveAs, setSaveAs] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Export runs the definition on screen, unsaved edits included, on the server (reportExport.ts); one at a time.
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState<string | null>(null);
  const [exporter] = useState(() => createReportExporter(authenticatedFetch, ({ exporting: open, outcome }) => {
    setExporting(open);
    if (open) { setExported(null); setProblem(null); }
    if (outcome?.error !== undefined) setProblem(outcome.error);
    else if (outcome) setExported(outcome.message);
  }));
  const definition = draft ?? savedDefinition;
  // The run is scoped to the visit: rows another report (or an earlier visit) ran never count as this visit's.
  const { result, rowCap, error: runError, current: ranCurrent } = useRun(definition, type, version, visit === null ? '' : `visit ${visit}`);
  // Opening a saved report's own page, once its rows have run, records the open for the library's
  // Recent view (PUT /api/reports/saved/:id/run, MOTHER-304), once per visit. A failed record is
  // dropped: it never shows, retries or holds up the report.
  const recorded = useRef<number | null>(null);
  useEffect(() => {
    if (isNew || editing || !saved || visit === null || !result || !ranCurrent || recorded.current === visit) return;
    recorded.current = visit;
    markReportRun(authenticatedFetch, saved.id).catch(() => undefined);
  }, [isNew, editing, saved, visit, result, ranCurrent]);
  const cell = useCellRows(definition);
  // A cell saved in place writes its record as a list view would, then the report runs again so totals follow.
  const saveCell = async (row: Record<string, unknown>, field: { key: string }, value: unknown) => {
    const target = targets.get(field.key);
    if (!target) throw new Error('This field cannot be changed from a report.');
    const { id, body } = cellEdit(target, row, value);
    await updateRecord(authenticatedFetch, target.object, id, body);
    setVersion((v) => v + 1);
    cell.refresh();
  };
  const dirty = isNew || (draft !== null && JSON.stringify(draft) !== JSON.stringify(savedDefinition));

  if (typesError || types === null || loading) {
    return (
      <PageContent as="section" width="wide">
        {typesError ? <InlineMessage tone="error" role="alert">{typesError}</InlineMessage> : <Spinner />}
      </PageContent>
    );
  }
  if ((!saved && !isNew) || !definition || !type) {
    return (
      <PageContent as="section" width="wide">
        <InlineMessage tone="error">{opened?.error ?? 'This report doesn’t exist, or you can’t see it.'}</InlineMessage>
      </PageContent>
    );
  }
  const editable = isNew || saved?.canEdit === true;
  const change = (next: ReportDefinition) => setDraft(next);

  return (
    <PageContent as="section" width="wide">
      <Stack>
        {problem ? <InlineMessage tone="error" role="alert">{problem}</InlineMessage> : null}
        {runError ? <InlineMessage tone="error" role="alert">{runError}</InlineMessage> : null}
        {exported ? <InlineMessage tone="info">{exported}</InlineMessage> : null}
        {rowCap !== null && result ? (
          <InlineMessage tone="info">
            Showing the first {rowCap.toLocaleString('en-US')} of {result.totalRows.toLocaleString('en-US')} records, the most a report lists. Groups and totals cover them all.
          </InlineMessage>
        ) : null}
        <ReportRunHeader
          definition={definition}
          reportType={type}
          result={result}
          onNameChange={(name) => change({ ...definition, name })}
          onDescriptionChange={(description) => {
            const { description: _previous, ...rest } = definition;
            change(description ? { ...rest, description } : rest);
          }}
          actions={
            <>
              <ReportEditButtons definition={definition} reportType={type} onChange={change} />
              {dirty ? (
                <>
                  <Button variant="subtle" onClick={() => { if (isNew) navigate(REPORTS_ROUTE); else { setDraft(null); setProblem(null); } }}>Discard</Button>
                  {!isNew ? <Button variant={editable ? 'neutral' : 'brand'} onClick={() => setSaveAs(true)}>Save as</Button> : null}
                  {editable ? (
                    <Button
                      variant="brand"
                      disabled={saving}
                      onClick={() => {
                        if (isNew || !saved) { setSaveAs(true); return; }
                        setSaving(true);
                        saveReport(authenticatedFetch, saved.id, definition).then(
                          (report) => { setOpened({ id: report.id, visit: visits.current, report, error: null }); setDraft(null); setProblem(null); },
                          (err: unknown) => setProblem(errorText(err)),
                        ).finally(() => setSaving(false));
                      }}
                    >
                      Save
                    </Button>
                  ) : null}
                </>
              ) : null}
            </>
          }
          onExport={() => exporter.start(definition)}
          exporting={exporting}
          timeZone={TIME_ZONE}
        />
        {result ? (
          <ReportView
            definition={definition}
            reportType={type}
            result={result}
            timeZone={TIME_ZONE}
            onSortChange={(sort) => change({ ...definition, sort })}
            onSummariesChange={(summaries) => change({ ...definition, summaries })}
            recordHref={recordHref}
            onNavigate={navigate}
            onCellSave={targets.size > 0 ? saveCell : undefined}
            onMatrixCellClick={cell.select}
            selectedCell={cell.selected}
            cellRows={cell.rows}
            onMoreCellRows={cell.more}
            onGroupSortChange={(index, direction) =>
              change({ ...definition, groupRows: definition.groupRows.map((g, i) => (i === index ? { ...g, sort: direction } : g)) })
            }
            // By its values, or by Record Count or a summary shown: saved with the report, and the server orders rows the same way.
            onGroupingSortChange={(index, { sort, sortBy }) => change({
              ...definition,
              groupRows: definition.groupRows.map((g, i) => {
                if (i !== index) return g;
                const { sortBy: _previous, ...rest } = g;
                return sortBy ? { ...rest, sort, sortBy } : { ...rest, sort };
              }),
            })}
          />
        ) : runError ? null : (
          <Spinner />
        )}
        <SaveReportDialog
          open={saveAs}
          onOpenChange={setSaveAs}
          title={isNew ? 'Save report' : 'Save a copy'}
          defaultName={isNew ? definition.name : `${definition.name} (copy)`}
          folders={library?.folders ?? []}
          defaultFolderId={saved?.folderId ?? params.get('folder') ?? undefined}
          onCreateFolder={async ({ name, parentId }) => {
            const folder = await createFolder(authenticatedFetch, name, parentId, false);
            void reloadLibrary();
            return folder.id;
          }}
          onSave={async ({ name, folderId }) => {
            const report = await createReport(authenticatedFetch, folderId, { ...definition, name });
            setDraft(null);
            setProblem(null);
            navigate(`${REPORTS_ROUTE}/${report.id}`);
          }}
        />
      </Stack>
    </PageContent>
  );
}
